import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { communities } from '@/mocks/communities';
import { useSEO } from '@/hooks/useSEO';
import TopAdBanner from './components/TopAdBanner';
import SiteHeader from '@/components/feature/SiteHeader';
import Sidebar from './components/Sidebar';
import CommunityCard from './components/CommunityCard';
import AdBanner from './components/AdBanner';
import HeroFeaturedBanner from './components/HeroFeaturedBanner';
import PartnershipPanel from './components/PartnershipPanel';
import Footer from './components/Footer';
import ScrollBatteryBar from '@/components/feature/ScrollBatteryBar';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import GlowButton from '@/components/base/GlowButton';


const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

interface LetterEmptyStateProps {
  letter: string;
  onLetterChange: (l: string | null) => void;
}

function LetterEmptyState({ letter, onLetterChange }: LetterEmptyStateProps) {
  const idx = ALPHABET.indexOf(letter);

  // Find up to 3 nearby letters (before + after) that actually have communities
  const nearby: string[] = [];
  for (let offset = 1; offset <= 13 && nearby.length < 4; offset++) {
    const prev = ALPHABET[idx - offset];
    const next = ALPHABET[idx + offset];
    if (prev) {
      const hasPrev = communities.some((c) => c.name.toUpperCase().startsWith(prev));
      if (hasPrev) nearby.push(prev);
    }
    if (nearby.length < 4 && next) {
      const hasNext = communities.some((c) => c.name.toUpperCase().startsWith(next));
      if (hasNext) nearby.push(next);
    }
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center px-4">
      {/* Big letter display */}
      <div className="relative mb-6">
        <div className="w-24 h-24 flex items-center justify-center rounded-3xl bg-slate-100/80 border border-slate-200/60">
          <span className="text-5xl font-black text-slate-200 select-none">{letter}</span>
        </div>
        <div className="absolute -bottom-1.5 -right-1.5 w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 border border-amber-200/70">
          <i className="ri-search-line text-amber-500 text-sm"></i>
        </div>
      </div>

      <h3 className="text-lg font-bold text-slate-700 mb-1.5">
        No communities start with &ldquo;<span className="text-[#0088CC]">{letter}</span>&rdquo;
      </h3>
      <p className="text-sm text-slate-400 max-w-xs mb-7 leading-relaxed">
        Looks like we don&apos;t have any listed communities beginning with this letter yet. Try a nearby letter below!
      </p>

      {nearby.length > 0 && (
        <div className="mb-8">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Try these letters</p>
          <div className="flex items-center gap-2 justify-center flex-wrap">
            {nearby.map((l) => {
              const count = communities.filter((c) => c.name.toUpperCase().startsWith(l)).length;
              return (
                <button
                  key={l}
                  onClick={() => onLetterChange(l)}
                  className="group flex flex-col items-center gap-0.5 px-4 py-2.5 rounded-xl bg-white border border-slate-200/80 hover:border-[#0088CC]/40 hover:bg-[#0088CC]/5 transition-all cursor-pointer"
                >
                  <span className="text-xl font-black text-slate-700 group-hover:text-[#0088CC] transition-colors leading-none">{l}</span>
                  <span className="text-[10px] text-slate-400 group-hover:text-[#0088CC]/70 transition-colors whitespace-nowrap">
                    {count} {count === 1 ? 'community' : 'communities'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button
          onClick={() => onLetterChange(null)}
          className="flex items-center gap-2 text-sm text-[#0088CC] hover:underline cursor-pointer whitespace-nowrap"
        >
          <i className="ri-list-unordered text-base w-4 h-4 flex items-center justify-center"></i>
          View all communities
        </button>
        <span className="hidden sm:block text-slate-300">·</span>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('open-submit-modal'))}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-add-circle-line text-base w-4 h-4 flex items-center justify-center"></i>
          Submit a community starting with {letter}
        </button>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedRegion, setSelectedRegion] = useState('All');
  const [selectedSize, setSelectedSize] = useState('All');
  const [selectedActivity, setSelectedActivity] = useState('All');
  const [selectedSort, setSelectedSort] = useState('Most Members');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const openSubmitModal = () => window.dispatchEvent(new CustomEvent('open-submit-modal'));
  const [visibleCount, setVisibleCount] = useState(6);
  const PAGE_SIZE = 6;

  // A–Z letter filter — read from URL ?letter=
  const selectedLetter = searchParams.get('letter')?.toUpperCase() ?? null;

  const handleLetterChange = (letter: string | null) => {
    if (letter === null) {
      searchParams.delete('letter');
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ letter }, { replace: true });
    }
    setVisibleCount(PAGE_SIZE);
  };

  useSEO({
    title: 'TGDirectory – Find & Join the Best Telegram Communities',
    description: 'Browse 500+ curated Telegram communities by category, region, and activity level. Discover active Telegram groups for IT, Finance, Games, Health, Arts and more.',
    keywords: 'Telegram communities, Telegram groups, Telegram directory, join Telegram, best Telegram channels, find Telegram group',
    canonical: 'https://tgdirectory.app/',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'TGDirectory – Telegram Community Directory',
      url: 'https://tgdirectory.app/',
      description: 'The #1 Telegram community directory. Browse and join the best Telegram groups.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: 'https://tgdirectory.app/' }],
      },
    },
  });

  const mainContentRef = useScrollReveal<HTMLDivElement>({ delay: 80 });
  const rightPanelRef = useScrollReveal<HTMLElement>({ delay: 160 });
  const footerRef = useScrollReveal<HTMLDivElement>({ delay: 0 });

  // 手机侧边栏打开时锁定主页面滚动
  useEffect(() => {
    if (mobileFilterOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [mobileFilterOpen]);

  const filtered = useMemo(() => {
    let list = [...communities];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) => c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q) || c.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (selectedCategory !== 'All') list = list.filter((c) => c.category === selectedCategory);
    if (selectedRegion !== 'All') list = list.filter((c) => c.region === selectedRegion);
    if (selectedActivity !== 'All') list = list.filter((c) => c.activityLevel === selectedActivity.toLowerCase());
    if (selectedSize !== 'All') {
      list = list.filter((c) => {
        if (selectedSize === 'Small (<500)') return c.memberCount < 500;
        if (selectedSize === 'Medium (500-5000)') return c.memberCount >= 500 && c.memberCount <= 5000;
        if (selectedSize === 'Large (>5000)') return c.memberCount > 5000;
        return true;
      });
    }
    // A–Z letter filter
    if (selectedLetter) {
      list = list.filter((c) => c.name.toUpperCase().startsWith(selectedLetter));
    }

    if (selectedSort === 'Most Members') list.sort((a, b) => b.memberCount - a.memberCount);
    else if (selectedSort === 'Newest') list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    else if (selectedSort === 'A-Z') list.sort((a, b) => a.name.localeCompare(b.name));
    else if (selectedSort === 'Most Active') list.sort((a, b) => {
      const order = { high: 0, medium: 1, low: 2 };
      return order[a.activityLevel] - order[b.activityLevel];
    });

    return list;
  }, [searchQuery, selectedCategory, selectedRegion, selectedSize, selectedActivity, selectedSort, selectedLetter]);

  // Reset visible count when filter changes
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [searchQuery, selectedCategory, selectedRegion, selectedSize, selectedActivity, selectedSort, selectedLetter]);



  const visibleList = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const renderGrid = () => {
    const items: JSX.Element[] = [];
    visibleList.forEach((community, i) => {
      items.push(<CommunityCard key={community.id} community={community} />);
      if ((i + 1) % 6 === 0 && i < visibleList.length - 1) {
        items.push(<AdBanner key={`ad-${i}`} variant="inline" />);
      }
    });
    return items;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] text-slate-800 font-sans">
      <ScrollBatteryBar />
      <TopAdBanner />
      <SiteHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onMobileMenuToggle={() => {}}
        onMobileFilterToggle={() => setMobileFilterOpen(true)}
      />

      {/* Hero Featured Banner — full width, below nav */}
      <HeroFeaturedBanner />

      {/* Advertise with us strip */}
      <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pt-6">
        <div className="relative overflow-hidden rounded-2xl bg-white/45 backdrop-blur-md border border-[#b8d4ea]/50 px-6 py-4 flex flex-col sm:flex-row items-center gap-4">
          {/* Decorative blobs */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            <div className="absolute -right-6 -top-6 w-40 h-40 rounded-full bg-[#0088CC]/8"></div>
            <div className="absolute right-16 bottom-0 w-24 h-24 rounded-full bg-[#0088CC]/6"></div>
          </div>

          <div className="flex items-center gap-3 shrink-0 relative z-10">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0088CC]/12 backdrop-blur-sm border border-[#0088CC]/20">
              <i className="ri-advertisement-line text-[#0088CC] text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 whitespace-nowrap">Advertise on TGDirectory</p>
              <p className="text-xs text-slate-500 hidden sm:block">Reach 50,000+ Telegram community seekers every day</p>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-6 flex-1 justify-center relative z-10">
            {[
              { icon: 'ri-eye-line', val: '50k+', label: 'Daily Views' },
              { icon: 'ri-group-line', val: '200k+', label: 'Monthly Users' },
              { icon: 'ri-global-line', val: '80+', label: 'Countries' },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/60 backdrop-blur-sm border border-[#b8d4ea]/50">
                  <i className={`${s.icon} text-[#0088CC] text-sm`}></i>
                </span>
                <div>
                  <p className="text-sm font-extrabold text-slate-800 leading-none">{s.val}</p>
                  <p className="text-[10px] text-slate-400">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="relative z-10 shrink-0">
            <GlowButton
              as="a"
              href="https://t.me/liketgli"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-sm font-bold px-5 py-2.5 rounded-xl whitespace-nowrap"
            >
              <i className="ri-telegram-fill text-base"></i>
              Get Ad Placement
            </GlowButton>
          </div>
        </div>
      </div>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-7 relative">
        <div className="flex gap-6">
          {/* Sidebar */}
          <Sidebar
            selectedCategory={selectedCategory}
            selectedRegion={selectedRegion}
            selectedSize={selectedSize}
            selectedActivity={selectedActivity}
            selectedSort={selectedSort}
            onCategoryChange={setSelectedCategory}
            onRegionChange={setSelectedRegion}
            onSizeChange={setSelectedSize}
            onActivityChange={setSelectedActivity}
            onSortChange={setSelectedSort}
            onResetAll={() => {
              setSelectedCategory('All');
              setSelectedRegion('All');
              setSelectedSize('All');
              setSelectedActivity('All');
              setSelectedSort('Most Members');
              handleLetterChange(null);
            }}
            isMobileOpen={mobileFilterOpen}
            onMobileClose={() => setMobileFilterOpen(false)}
          />

          {/* Main Content */}
          <div ref={mainContentRef} className="flex-1 min-w-0 bg-white/50 backdrop-blur-sm rounded-2xl border border-white/90 px-3 sm:px-5 py-4 sm:py-5">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-slate-400 mb-4">
              <a href="#" className="hover:text-slate-600 transition-colors">Home</a>
              <span>/</span>
              <span className="text-slate-600">Community Directory</span>
              {selectedCategory !== 'All' && <><span>/</span><span className="text-[#0088CC]">{selectedCategory}</span></>}
              {selectedLetter && <><span>/</span><span className="text-[#0088CC]">Starts with &ldquo;{selectedLetter}&rdquo;</span></>}
            </nav>

            {/* Letter filter active banner */}
            {selectedLetter && (
              <div className="flex items-center justify-between bg-[#0088CC]/8 border border-[#0088CC]/20 rounded-xl px-4 py-2.5 mb-4">
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#0088CC] text-white text-sm font-bold">{selectedLetter}</span>
                  <span className="text-sm text-slate-600">
                    Browsing communities starting with <strong className="text-[#0088CC]">&ldquo;{selectedLetter}&rdquo;</strong>
                  </span>
                </div>
                <button
                  onClick={() => handleLetterChange(null)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap ml-3"
                >
                  <i className="ri-close-line text-sm w-4 h-4 flex items-center justify-center"></i>
                  Clear
                </button>
              </div>
            )}

            {/* Stats bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <div>
                <h1 className="text-xl font-bold text-slate-800 tracking-tight">
                  {selectedLetter
                    ? `Communities: ${selectedLetter}`
                    : selectedCategory === 'All'
                      ? 'All Communities'
                      : `${selectedCategory} Communities`}
                </h1>
                <p className="text-sm text-slate-400 mt-0.5">
                  {filtered.length} communities found
                  {searchQuery && <span> for &quot;<strong className="text-slate-600">{searchQuery}</strong>&quot;</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <i className="ri-information-line w-4 h-4 flex items-center justify-center"></i>
                <span>Updated daily</span>
              </div>
            </div>

            {/* Community Grid */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {renderGrid()}
              </div>
            ) : selectedLetter ? (
              // Empty state specifically for A–Z letter filter
              <LetterEmptyState
                letter={selectedLetter}
                onLetterChange={handleLetterChange}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#0088CC]/10 mb-4">
                  <i className="ri-search-line text-2xl text-[#0088CC]"></i>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No communities found</h3>
                <p className="text-slate-400 text-sm">Try adjusting your filters or search query.</p>
                <button
                  onClick={() => { setSearchQuery(''); setSelectedCategory('All'); setSelectedRegion('All'); setSelectedSize('All'); setSelectedActivity('All'); handleLetterChange(null); }}
                  className="mt-4 text-sm text-[#0088CC] hover:underline cursor-pointer whitespace-nowrap"
                >
                  Reset all filters
                </button>
              </div>
            )}

            {/* Load More */}
            {hasMore && (
              <div className="mt-6 flex flex-col items-center gap-2">
                <p className="text-xs text-slate-400">
                  Showing <strong className="text-slate-600">{visibleList.length}</strong> of <strong className="text-slate-600">{filtered.length}</strong> communities
                </p>
                <GlowButton
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="px-8 py-2.5 text-sm font-semibold rounded-full"
                  variant="secondary"
                >
                  <i className="ri-arrow-down-line text-base"></i>
                  Load More Communities
                </GlowButton>
              </div>
            )}
            {!hasMore && filtered.length > PAGE_SIZE && (
              <p className="text-center text-xs text-slate-400 mt-6">All {filtered.length} communities loaded</p>
            )}

            {/* Bottom Ad */}
            {filtered.length > 0 && (
              <div className="mt-8">
                <AdBanner variant="bottom" />
              </div>
            )}
          </div>

          {/* Right Panel */}
          <aside ref={rightPanelRef} className="hidden xl:flex flex-col w-72 shrink-0 sticky top-20 h-[calc(100vh-5rem)] overflow-y-auto gap-4 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
            <PartnershipPanel />

            {/* Side Ad */}
            <div className="rounded-2xl bg-white/70 backdrop-blur-md overflow-hidden">
              <div className="px-3 py-1.5 border-b border-slate-100/80">
                <span className="text-[10px] font-semibold text-[#0088CC] uppercase tracking-wider">Advertisement</span>
              </div>
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=telegram%20bot%20service%20advertisement%20vertical%20banner%20light%20white%20background%20with%20blue%20telegram%20icon%20and%20promotional%20offer%20text%20clean%20minimal%20design&width=288&height=192&seq=302&orientation=portrait"
                  alt="Side Advertisement"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-3 text-center">
                <p className="text-xs text-slate-400">Advertise here</p>
                <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }} className="text-xs text-[#0088CC] hover:underline cursor-pointer">Contact us</a>
              </div>
            </div>

            {/* Submit CTA */}
            <div id="submit" className="rounded-2xl bg-white/45 backdrop-blur-md border border-white/80 p-5 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0088CC]/12 backdrop-blur-sm border border-[#0088CC]/20 mx-auto mb-3">
                <i className="ri-add-circle-line text-[#0088CC] text-xl"></i>
              </div>
              <h3 className="text-sm font-bold text-slate-700 mb-1">Submit Your Community</h3>
              <p className="text-xs text-slate-400 mb-3">Have a Telegram community? Get it listed and reach thousands of users.</p>
              <GlowButton
                onClick={openSubmitModal}
                className="w-full py-2.5 text-sm font-bold rounded-xl"
              >
                Submit Community
              </GlowButton>
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile FABs */}
      <div className="xl:hidden fixed bottom-6 right-4 z-40 flex flex-col gap-3" style={{ position: 'fixed' }}>
        {/* Submit FAB */}
        <GlowButton
          onClick={openSubmitModal}
          className="w-14 h-14 rounded-full"
          title="Submit Community"
        >
          <i className="ri-add-line text-xl"></i>
        </GlowButton>
        {/* Filter FAB */}
        <GlowButton
          onClick={() => setMobileFilterOpen(true)}
          className="w-14 h-14 rounded-full"
        >
          <i className="ri-filter-3-line text-xl"></i>
        </GlowButton>
      </div>

      <Footer />

    </div>
  );
}
